import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithReset, selectAtom } from 'jotai/utils';

import { demographicMapAtom } from '@/_atoms/demographic';
import { surveyViewAtom } from '@/_atoms/survey';
import { elementIndexAtom, contextNavAtom, stepNavAtom } from '@/_atoms/navigation';
import { checkMaxSecondsBetweenSaves, getDateOfFirstUnsavedAnswer } from '@/_helpers/answerHelpers';

import { Answer, Demographic } from '@/types/respondent';
import type { ElementContext, StepInfo, SurveyView } from '@/types/survey';

/** A map of item IDs to Answer objects indicating the respondent's answer to each item. */
export const answerMapAtom = atomWithReset<Map<string, Answer[]>>(new Map());

/** Atom storing the last error encountered when attempting to save answers. */
export const answerErrorAtom = atomWithReset<Error | null>(null);

/**
 * Returns a derived atom that computes the most recent non-deleted answer for a given item.
 *
 * @param itemId - The ID of the item to find the answer for.
 * @returns An atom representing the most recent non-deleted answer, or null if no answer is found.
 */
export const currentAnswerAtomFamily = atomFamily((itemId: string) =>
  selectAtom(answerMapAtom, (map: Map<string, Answer[]>): Answer | null =>
    map.get(itemId)?.find(a => !a.deletedAt) ?? null
  )
);

/** Atom containing the set of answer IDs currently being saved. */
export const savingAnswerIdsAtom = atomWithReset<Set<string>>(new Set<string>());

/**
 * Set of element IDs (items or fields) that currently have at least one non-deleted reply.
 *
 * - Items contribute their item id from answerMapAtom
 * - Fields contribute their field id from demographicMapAtom
 * - Instructions are never included (they won't appear in either map)
 */
export const answeredElementsAtom = atom<Set<string>>((get) => {
  const answerMap: Map<string, Answer[]> = get(answerMapAtom);
  const demographicMap: Map<string, Demographic[]> = get(demographicMapAtom);

  const answered = new Set<string>();

  for (const [itemId, answers] of answerMap.entries()) {
    if (answers.some(a => !a.deletedAt)) {
      answered.add(itemId);
    }
  }

  for (const [fieldId, demographics] of demographicMap.entries()) {
    if (demographics.some(d => !d.deletedAt)) {
      answered.add(fieldId);
    }
  }

  return answered;
});

/**
 * Map of unsaved answers keyed by answer ID.
 * - Key: answerId (string)
 * - Value: itemId (string) for the answer
 */
export const unsavedAnswersAtom = atomWithReset<Map<string, string>>(new Map<string, string>());

/** Determine if it is time to save all unsaved answers. */
export const isTimeToSaveAnswersAtom = atom<boolean>((get) => {
  const unsaved: Map<string, string> = get(unsavedAnswersAtom);

  // No unsaved answers → nothing to save
  if (unsaved.size === 0) return false;

  // No survey loaded → save eagerly
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  const currentStep: StepInfo | null = get(stepNavAtom).currentStep;
  if (!currentContext || !currentStep) return true;

  // No survey loaded → save eagerly
  const surveyView: SurveyView | null | undefined = get(surveyViewAtom)?.data;
  if (!surveyView) return true;

  // Enough unsaved answers → save
  if (unsaved.size >= currentStep.maxAnswersBetweenSaves) return true;

  // Any unsaved answer from a different step → save
  const { elements, contextMap } = surveyView;
  const hasAnswerFromDifferentStep = Array.from(unsaved.values()).some(
    (itemId) => contextMap.get(itemId)?.step !== currentContext.step
  );
  if (hasAnswerFromDifferentStep) return true;

  // Past the item section (the current element is a demographic) → save
  const elementIndex: number = get(elementIndexAtom);
  if ('category' in elements[elementIndex]) return true;

  // Check if `maxSecondsBetweenSaves` has been exceeded
  if (!currentStep.maxSecondsBetweenSaves) return false;

  const answerMap: Map<string, Answer[]> = get(answerMapAtom);
  const firstUnsavedAt: Date | null = getDateOfFirstUnsavedAnswer(unsaved, answerMap);
  return checkMaxSecondsBetweenSaves(currentStep.maxSecondsBetweenSaves, new Date(), firstUnsavedAt);
});